import { Tooltip } from '@mui/material';
import { cloneElement, isValidElement, useId, type ReactElement } from 'react';

type DisabledWithTooltipProps = {
  /** When true, the child is forced disabled and the reason is exposed. */
  disabled: boolean;
  /** The explanation, already translated by the caller. */
  reason: string;
  /** The control to gate. Its own `disabled` prop is overridden when disabled. */
  children: ReactElement;
};

/**
 * The "disabled control plus explanatory tooltip" pattern shared by the
 * Library, Plan, and Actuals permission surfaces. Wrapping (not rewriting)
 * the control keeps aria wiring in one place: the reason is rendered as a
 * visually-hidden element associated with the control through
 * `aria-describedby`, so it is reachable by assistive tech without hover,
 * while MUI Tooltip provides the on-hover presentation.
 */
export function DisabledWithTooltip({
  disabled,
  reason,
  children,
}: DisabledWithTooltipProps) {
  const explanationId = useId();

  if (!disabled || !isValidElement(children)) {
    return children;
  }

  // Disabled elements do not emit pointer events, so the Tooltip needs a
  // wrapper span to anchor to; the describedby association sits on the
  // control itself so screen readers announce the reason with the control.
  //
  // The reason is injected at BOTH levels: top-level for controls whose root
  // is the focusable element (Button, IconButton, MenuItem, ...), and inside
  // `inputProps` for MUI form controls that declare them (Switch, Checkbox,
  // TextField) — MUI's SwitchBase renders those as a nested <input>, and
  // top-level props land on a non-focusable root span, so the association
  // would never reach the focused control.
  //
  // Precondition: the inputProps branch is keyed on the child already
  // declaring inputProps. A bare <Switch /> without inputProps would fall
  // through to the top-level branch and not be announced from the input —
  // pass at least `{ 'aria-label': ... }` when gating such a control.
  // The top-level ID is kept in both branches deliberately: it is harmless
  // duplication on SwitchBase roots and load-bearing for focusable roots.
  const childProps = children.props as {
    inputProps?: Record<string, unknown>;
  };
  // An existing describedby (e.g. the ID MUI generates for a TextField's
  // helperText) is preserved: the reason ID is appended to the list, not
  // substituted for it.
  const inputDescribedBy = [
    (childProps.inputProps?.['aria-describedby'] as string | undefined) ?? '',
    explanationId,
  ]
    .filter(Boolean)
    .join(' ');
  const gatedChild = cloneElement(
    children,
    childProps.inputProps !== undefined
      ? ({
          disabled: true,
          'aria-describedby': explanationId,
          inputProps: {
            ...childProps.inputProps,
            'aria-describedby': inputDescribedBy,
          },
        } as Record<string, unknown>)
      : ({
          disabled: true,
          'aria-describedby': explanationId,
        } as Record<string, unknown>),
  );

  return (
    <Tooltip title={reason} arrow>
      <span className="inline-flex">
        {gatedChild}
        <span id={explanationId} className="sr-only">
          {reason}
        </span>
      </span>
    </Tooltip>
  );
}
